import OrderCargoOrderDetailsComplex, {
  OrderCargoOrderDetailsComplexCandidatureDriver,
  OrderCargoOrderDetailsComplexPassenger,
  OrderCargoOrderDetailsComplexSolveNode,
  OrderCargoOrderDetailsComplexTransportingOrder,
  OrderCargoOrderDetailsComplexTransportingOrderDriver,
} from "../../../../common/services/order/cargo-order/details-complex/order-cargo-order-details-complex";
import SourceType from "../../../../common/types/source-type";
import OrderDetailsCargoOrder, {
  OrderDetailsCargoOrderAddress,
} from "./types/order-details-cargo-order";
import OrderDetailsDriver, {
  OrderDetailsDriverRole,
} from "./types/order-details-driver";
import OrderDetailsPassenger from "./types/order-details-passenger";

const createAssignedDriver = (
  responseDriver: OrderCargoOrderDetailsComplexTransportingOrderDriver,
): OrderDetailsDriver => {
  return {
    uuid: responseDriver.uuid,
    firstName: responseDriver.firstName,
    lastName: responseDriver.lastName,
    displayName: `${responseDriver.firstName} ${responseDriver.lastName}`,
    phoneNumber: `${responseDriver.mobilePrimaryPrefix} ${responseDriver.mobilePrimary}`,
    role: OrderDetailsDriverRole.ASSIGNED,
  };
};

const createCandidateDriver = (
  responseCandidateDriver: OrderCargoOrderDetailsComplexCandidatureDriver,
): OrderDetailsDriver => {
  return {
    uuid: responseCandidateDriver.uuid,
    firstName: responseCandidateDriver.firstName,
    lastName: responseCandidateDriver.lastName,
    displayName: `${responseCandidateDriver.firstName} ${responseCandidateDriver.lastName}`,
    phoneNumber: `${responseCandidateDriver.mobilePrimaryPrefix} ${responseCandidateDriver.mobilePrimary}`,
    role: OrderDetailsDriverRole.CANDIDATE,
  };
};

const createDrivers = (
  responseTransportingOrders: OrderCargoOrderDetailsComplexTransportingOrder[],
  responseCandidateDrivers: OrderCargoOrderDetailsComplexCandidatureDriver[],
) => {
  const responseDrivers = responseTransportingOrders
    .map((order) => order.driver)
    .filter(
      (driver) => driver,
    ) as OrderCargoOrderDetailsComplexTransportingOrderDriver[];

  const assignedDrivers = responseDrivers
    ? responseDrivers.map(createAssignedDriver)
    : [];
  const candidateDrivers = responseCandidateDrivers.map(createCandidateDriver);

  const drivers: OrderDetailsDriver[] = [
    ...assignedDrivers,
    ...candidateDrivers,
  ];

  return drivers;
};

const createPassenger = (
  responsePassenger: OrderCargoOrderDetailsComplexPassenger,
): OrderDetailsPassenger => {
  return {
    uuid:
      responsePassenger.itemType === SourceType.EXTERNAL
        ? responsePassenger.cargoItemId
        : responsePassenger.passengerUuid,
    displayName: `${responsePassenger.firstName} ${responsePassenger.lastName}`,
    phoneNumber: `${responsePassenger.phonePrefix} ${responsePassenger.phoneNumber}`,
  };
};

const createPassengers = (
  transportingOrders: OrderCargoOrderDetailsComplexTransportingOrder[],
): OrderDetailsPassenger[] => {
  const responsePassengers = transportingOrders
    .map((order) => order.passengers)
    .flat();

  const passengers = responsePassengers.map(createPassenger);

  return passengers;
};

const createAddress = (
  node: OrderCargoOrderDetailsComplexSolveNode,
): OrderDetailsCargoOrderAddress => {
  return {
    lat: node.lat,
    lon: node.lon,
    estimatedTime: node.estimatedTime,
  };
};

const createBasicInfo = (
  responseCargoOrder: OrderCargoOrderDetailsComplex,
): OrderDetailsCargoOrder => {
  const transportingOrderWithDriver =
    responseCargoOrder.transportingOrders.find(
      (order: OrderCargoOrderDetailsComplexTransportingOrder) => !!order.driver,
    );

  const solvedOrderId = transportingOrderWithDriver?.uuid;
  const routeNumber = transportingOrderWithDriver?.planEntryHumanId;

  let firstNode: OrderDetailsCargoOrderAddress | undefined;

  const firstSolve = responseCargoOrder.transportingOrders[0].solve[0];
  if (Array.isArray(firstSolve)) {
    firstNode = createAddress(firstSolve[0]);
  } else if (firstSolve) {
    firstNode = createAddress(firstSolve);
  }

  return {
    cargoOrderId: responseCargoOrder.uuid,
    cargoCompanyOrderId: responseCargoOrder.humanId,
    cargoCompanyExternalOrderId: responseCargoOrder.externalId,
    changesApprovals: {
      isDealerApproveNeeded: true,
      isDispatcherApproveNeeded: true,
      isOperatorApproveNeeded: true,
    },
    isCancelRequestSent: responseCargoOrder.isCancelRequested,
    isCancelled: responseCargoOrder.isCancelled,
    isFinished: responseCargoOrder.isFinished,
    taxiCorporationUuid: responseCargoOrder.targetTaxiUuid,
    dispatcherUuid: responseCargoOrder.dispatcher.uuid,
    isPublished: responseCargoOrder.isPublished ?? true,
    solvedOrderId: solvedOrderId,
    transportingOrderUuid: responseCargoOrder.transportingOrders[0].uuid,
    routeNumber: routeNumber ?? null,
    contactPassengerAfterMinutes:
      responseCargoOrder.contactPassengerAfterMinutes,
    cancelByTaxiAllowed: responseCargoOrder.cancelByTaxiAllowed,
    editByTaxiAllowed: responseCargoOrder.editByTaxiAllowed,
    startingAddress: firstNode!,
  };
};

const orderDetailsFactory = {
  createPassengers,
  createDrivers,
  createBasicInfo,
};

export default orderDetailsFactory;
